import { Box, ButtonBase, TableHead, TableRow } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import type { Sort, SortValue } from '../model';
import { CourseStageHeader } from '../shared';
import {
    visiblePatientListColumns,
    PatientListHeadCell,
} from './PatientListTable';

type PatientListHeaderProps = {
    compact: boolean;
    sort: Sort;
    onSortChange: (key: SortValue) => void;
};

/** The sorted column's direction, as the aria-sort value for its header cell. */
function ariaSort(
    sort: Sort,
    key: SortValue
): 'ascending' | 'descending' | 'none' {
    if (sort.key !== key) {
        return 'none';
    }
    return sort.direction === 'asc' ? 'ascending' : 'descending';
}

/** Sticky column-header row; PATIENT_LIST_COLUMNS order, mirrored by PatientRow. */
export default function PatientListHeader({
    compact,
    sort,
    onSortChange,
}: PatientListHeaderProps) {
    const intl = useIntl();
    return (
        <TableHead>
            <TableRow>
                {visiblePatientListColumns(compact).map((column) => {
                    const { sortKey } = column;
                    const label = intl.formatMessage({ id: column.labelId });
                    // Too narrow to read beside the open panel.
                    const names =
                        column.stageNames && !compact ? (
                            <CourseStageHeader />
                        ) : undefined;
                    return (
                        <PatientListHeadCell
                            key={column.id}
                            alignEnd={column.alignEnd}
                            aria-sort={
                                sortKey === undefined
                                    ? undefined
                                    : ariaSort(sort, sortKey)
                            }
                        >
                            {sortKey === undefined ? (
                                (names ?? label)
                            ) : (
                                <SortLabel
                                    label={label}
                                    sortKey={sortKey}
                                    sort={sort}
                                    onSortChange={onSortChange}
                                >
                                    {names}
                                </SortLabel>
                            )}
                        </PatientListHeadCell>
                    );
                })}
            </TableRow>
        </TableHead>
    );
}

type SortLabelProps = {
    label: string;
    sortKey: SortValue;
    sort: Sort;
    onSortChange: (key: SortValue) => void;
    /** What the header shows in place of the label, which then only names the control. */
    children?: ReactNode;
};

/**
 * Sortable column header. Clicking selects the column; clicking the active
 * column flips direction (the caret rotates). The API sorts a single key in a
 * fixed direction, so the flipped direction reverses the loaded rows. The
 * direction is announced by aria-sort on the owning header cell.
 */
function SortLabel({
    label,
    sortKey,
    sort,
    onSortChange,
    children,
}: SortLabelProps) {
    const intl = useIntl();
    const active = sort.key === sortKey;
    return (
        <ButtonBase
            aria-label={intl.formatMessage(
                { id: 'patientList.column.sortBy' },
                { column: label }
            )}
            onClick={() => onSortChange(sortKey)}
            sx={{
                font: 'inherit',
                letterSpacing: 'inherit',
                textTransform: 'inherit',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                width: children === undefined ? undefined : '100%',
                color: active ? 'text.primary' : 'inherit',
                transition: 'color 100ms',
                '&:hover': {
                    color: active ? 'primary.main' : 'text.primary',
                },
                '& .sort-caret': {
                    display: 'inline-flex',
                    opacity: active ? 1 : 0,
                    transition: 'opacity 100ms, transform 120ms',
                    transform:
                        active && sort.direction === 'asc'
                            ? 'rotate(180deg)'
                            : 'none',
                },
                '&:hover .sort-caret': { opacity: active ? 1 : 0.5 },
            }}
        >
            {children === undefined ? (
                label
            ) : (
                <>
                    <Box component="span" sx={visuallyHidden}>
                        {label}
                    </Box>
                    {children}
                </>
            )}
            <Box component="span" className="sort-caret">
                <ChevronDown size={12} />
            </Box>
        </ButtonBase>
    );
}
